/**
 * ColorShield ID — Visual Trust Signal
 *
 * AUTHORITY BOUNDARY (non-negotiable):
 * - This UI reflects runtime/security state already decided by ColorShield.
 * - It does NOT authorize submission.
 * - Removing, hiding, or rewriting this DOM must never create PERMIT / WARN_ALLOW,
 *   bypass Hard Block, alter server policy, or alter runtime validation.
 * - There is no public setter that can force VERIFIED / WARNING / BLOCKED.
 */

import type { RuntimeTrustState, SubmitGateState } from "./types";

/** Public visual states derived from existing runtime + submit-gate state. */
export type VisualTrustState =
  | "INITIALIZING"
  | "VERIFYING"
  | "VERIFIED"
  | "WARNING"
  | "BLOCKED"
  | "STALE";

export interface VisualTrustInputs {
  runtimeTrustState: RuntimeTrustState;
  submitGateState: SubmitGateState;
  /** Auth/legacy blocked (form missing, etc.) — never upgrades to VERIFIED. */
  authBlocked?: boolean;
  /** ISO timestamp from runtime proof expiry when known (public freshness only). */
  lastVerifiedAt?: string | null;
  hostname?: string | null;
  flowType?: string | null;
  policyMode?: string | null;
}

export interface VisualTrustDetail {
  state: VisualTrustState;
  label: string;
  message: string;
  hostname?: string;
  flowType?: string;
  policyMode?: string;
  lastVerifiedAt?: string;
}

export const VISUAL_TRUST_COPY: Record<
  VisualTrustState,
  { label: string; message: string }
> = {
  INITIALIZING: {
    label: "INITIALIZING",
    message: "Initializing protection",
  },
  VERIFYING: {
    label: "VERIFYING",
    message: "Verifying protected interaction",
  },
  VERIFIED: {
    label: "VERIFIED",
    message: "Protected interaction verified",
  },
  WARNING: {
    label: "WARNING",
    message: "Submission allowed under monitoring policy",
  },
  BLOCKED: {
    label: "BLOCKED",
    message: "Submission blocked for your protection",
  },
  STALE: {
    label: "STALE",
    message: "Verification needs refresh",
  },
};

/**
 * Single normalized visual state from existing engines.
 * Priority: BLOCKED > WARNING > VERIFYING > VERIFIED > STALE > INITIALIZING
 * VERIFIED requires currently valid runtime trust — never AuthState alone.
 */
export function deriveVisualTrustState(input: VisualTrustInputs): VisualTrustState {
  const gate = input.submitGateState;
  const rt = input.runtimeTrustState;

  // Hard Block / fail-closed always replace any prior VERIFIED display.
  if (gate === "BLOCKED" || gate === "ERROR" || input.authBlocked) {
    return "BLOCKED";
  }
  if (gate === "WARNING") {
    return "WARNING";
  }
  if (gate === "VALIDATING" || rt === "BOOTSTRAPPING") {
    return "VERIFYING";
  }
  // Live proof only — SDK load / flow key / past bootstrap are insufficient.
  if (rt === "VERIFIED") {
    return "VERIFIED";
  }
  // WARN (fail-open) with no obtainable proof: submissions proceed under monitoring.
  if (rt === "FAILED" && String(input.policyMode || "").toUpperCase() === "WARN") {
    return "WARNING";
  }
  if (rt === "STALE" || rt === "FAILED") {
    return "STALE";
  }
  return "INITIALIZING";
}

export function buildVisualTrustDetail(input: VisualTrustInputs): VisualTrustDetail {
  const state = deriveVisualTrustState(input);
  const copy = VISUAL_TRUST_COPY[state];
  const detail: VisualTrustDetail = {
    state,
    label: copy.label,
    message: copy.message,
  };
  if (input.hostname) detail.hostname = input.hostname;
  if (input.flowType) detail.flowType = input.flowType;
  if (input.policyMode) detail.policyMode = String(input.policyMode).toUpperCase();
  if (input.lastVerifiedAt) detail.lastVerifiedAt = input.lastVerifiedAt;
  return detail;
}

const STYLE_ID = "csid-trust-styles";

/** Compact namespaced CSS — no global element selectors. */
export const VISUAL_TRUST_CSS = `
.csid-trust-root {
  --csid-trust-ink: #1b2430;
  --csid-trust-muted: #5b6775;
  --csid-trust-line: #d7dde5;
  --csid-trust-ok: #0f7a4b;
  --csid-trust-warn: #8a5a00;
  --csid-trust-bad: #a11919;
  --csid-trust-info: #3d5a80;
  font-family: Georgia, "Times New Roman", Times, serif;
  color: var(--csid-trust-ink);
  font-size: 14px;
  line-height: 1.35;
  margin: 0 0 12px;
  max-width: 28rem;
}
.csid-trust-shell {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid var(--csid-trust-line);
  border-radius: 8px;
  padding: 10px 12px;
  background: #f8fafc;
}
.csid-trust-brand {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--csid-trust-muted);
  margin: 0;
}
.csid-trust-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.csid-trust-icon {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: #8899aa;
}
.csid-trust-label {
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
}
.csid-trust-msg {
  margin: 0;
  color: var(--csid-trust-muted);
  font-size: 13px;
}
.csid-trust-details {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--csid-trust-muted);
}
.csid-trust-details > summary {
  cursor: pointer;
  list-style: none;
  outline: none;
}
.csid-trust-details > summary::-webkit-details-marker { display: none; }
.csid-trust-details > summary:focus-visible {
  outline: 2px solid var(--csid-trust-info);
  outline-offset: 2px;
}
.csid-trust-details[open] > summary { margin-bottom: 4px; }
.csid-trust-dl {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 10px;
  margin: 0;
}
.csid-trust-dl dt { color: var(--csid-trust-muted); }
.csid-trust-dl dd { margin: 0; color: var(--csid-trust-ink); word-break: break-word; }
.csid-trust-state-INITIALIZING .csid-trust-icon,
.csid-trust-state-VERIFYING .csid-trust-icon { background: var(--csid-trust-info); }
.csid-trust-state-VERIFIED {
  border-color: #9fd6b8;
  background: #f1fbf5;
}
.csid-trust-state-VERIFIED .csid-trust-icon { background: var(--csid-trust-ok); }
.csid-trust-state-WARNING {
  border-color: #e6d29a;
  background: #fffbf0;
}
.csid-trust-state-WARNING .csid-trust-icon { background: var(--csid-trust-warn); }
.csid-trust-state-BLOCKED {
  border-color: #e2b6b6;
  background: #fff6f6;
}
.csid-trust-state-BLOCKED .csid-trust-icon { background: var(--csid-trust-bad); }
.csid-trust-state-STALE {
  border-color: #d0d5dc;
  background: #f3f4f6;
}
.csid-trust-state-STALE .csid-trust-icon { background: #6b7280; }
`.trim();

export function ensureVisualTrustStyles(doc: Document = document): void {
  if (typeof doc === "undefined") return;
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.textContent = VISUAL_TRUST_CSS;
  doc.head.appendChild(style);
}

/**
 * Render (or update) the Visual Trust Signal into a host element.
 * Never writes secrets, proofs, permits, or credentials into the DOM.
 */
export function renderVisualTrustSignal(
  host: HTMLElement,
  detail: VisualTrustDetail,
  options?: { instanceId?: string }
): void {
  ensureVisualTrustStyles(host.ownerDocument || document);

  const prev = host.getAttribute("data-csid-visual-state");
  const stateChanged = prev !== detail.state;

  host.className = "csid-trust-root";
  host.setAttribute("data-colorshield-trust", "");
  host.setAttribute("data-csid-visual-state", detail.state);
  if (options?.instanceId) {
    host.setAttribute("data-csid-instance", options.instanceId);
  }

  // Strip any attacker-injected secret-looking text on each render.
  host.replaceChildren();

  const shell = host.ownerDocument!.createElement("div");
  shell.className = `csid-trust-shell csid-trust-state-${detail.state}`;
  shell.setAttribute("role", "status");
  shell.setAttribute("aria-live", stateChanged ? "polite" : "off");
  shell.setAttribute(
    "aria-label",
    `ColorShield ID live validation status: ${detail.label}. ${detail.message}`
  );

  const brand = host.ownerDocument!.createElement("p");
  brand.className = "csid-trust-brand";
  brand.textContent = "ColorShield ID";

  const row = host.ownerDocument!.createElement("div");
  row.className = "csid-trust-row";

  const icon = host.ownerDocument!.createElement("span");
  icon.className = "csid-trust-icon";
  icon.setAttribute("aria-hidden", "true");

  const label = host.ownerDocument!.createElement("span");
  label.className = "csid-trust-label";
  label.textContent = detail.label;

  row.append(icon, label);

  const msg = host.ownerDocument!.createElement("p");
  msg.className = "csid-trust-msg";
  msg.textContent = detail.message;

  shell.append(brand, row, msg);

  const details = host.ownerDocument!.createElement("details");
  details.className = "csid-trust-details";
  const summary = host.ownerDocument!.createElement("summary");
  summary.textContent = "Live validation details";
  details.appendChild(summary);

  const dl = host.ownerDocument!.createElement("dl");
  dl.className = "csid-trust-dl";
  const rows: Array<[string, string]> = [
    ["State", detail.label],
  ];
  if (detail.hostname) rows.push(["Protected hostname", detail.hostname]);
  if (detail.flowType) rows.push(["Protected flow", detail.flowType]);
  if (detail.policyMode) rows.push(["Policy mode", detail.policyMode]);
  if (detail.lastVerifiedAt) rows.push(["Last verification", detail.lastVerifiedAt]);
  for (const [k, v] of rows) {
    const dt = host.ownerDocument!.createElement("dt");
    dt.textContent = k;
    const dd = host.ownerDocument!.createElement("dd");
    dd.textContent = v;
    dl.append(dt, dd);
  }
  details.appendChild(dl);
  shell.appendChild(details);

  host.appendChild(shell);
}

function isHtmlElement(value: unknown): value is HTMLElement {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as HTMLElement).setAttribute === "function" &&
    typeof (value as HTMLElement).appendChild === "function"
  );
}

/**
 * Resolve mount host: explicit selector → data-colorshield-trust → auto near form.
 * Avoids invasive rewriting of existing form fields.
 */
export function resolveTrustMountHost(opts: {
  document: Document;
  formEl: HTMLFormElement | null;
  selector?: string | null;
  formId?: string;
  instanceId: string;
}): HTMLElement | null {
  const doc = opts.document;
  if (opts.selector) {
    const el = doc.querySelector(opts.selector);
    if (isHtmlElement(el)) return el;
  }

  const scoped = opts.formId
    ? doc.querySelector(
        `[data-colorshield-trust][data-form="${opts.formId.replace(/"/g, "")}"]`
      )
    : null;
  if (isHtmlElement(scoped)) return scoped;

  // Prefer an unused empty mount marker (not already bound to another instance).
  const markers = Array.from(doc.querySelectorAll("[data-colorshield-trust]"));
  for (const m of markers) {
    if (!isHtmlElement(m)) continue;
    const bound = m.getAttribute("data-csid-instance");
    if (!bound || bound === opts.instanceId) return m;
  }

  if (opts.formEl) {
    const host = doc.createElement("div");
    host.setAttribute("data-colorshield-trust", "");
    host.setAttribute("data-csid-auto-mount", "1");
    if (opts.formId) host.setAttribute("data-form", opts.formId);
    opts.formEl.insertBefore(host, opts.formEl.firstChild);
    return host;
  }
  return null;
}

/** Scan rendered text for forbidden secret patterns (test + defense-in-depth). */
export function visualTrustDomContainsSecrets(host: HTMLElement): boolean {
  const text = host.textContent || "";
  const html = host.innerHTML || "";
  const patterns = [
    /cs_inst_/i,
    /cs_isec_/i,
    /colorshield-runtime-proof-v1/i,
    /colorshield-submit-permit-v1/i,
    /colorshield-warn-allow-v1/i,
    /-----BEGIN[ A-Z]*PRIVATE KEY-----/,
    /runtime_proof/i,
    /submission_permit/i,
    /warn_allow/i,
  ];
  return patterns.some((p) => p.test(text) || p.test(html));
}
